import { useState, useEffect, useRef } from 'react';
import { fetchSwedishRoadNetwork, SwedishRoadNetwork } from '../services/swedishRoadNetworkService';
import type { MapBounds } from '../types';
import { boundsOverlapRatio } from '../utils/boundsUtils';
import type { SupportedCountry } from '../utils/countryDetection';

const EMPTY: SwedishRoadNetwork = { type: 'FeatureCollection', features: [] };

export const useSwedishRoadNetwork = (
  active: boolean,
  zoom: number,
  currentViewBounds: MapBounds | null,
  viewportCountry?: SupportedCountry | null,
) => {
  const [network, setNetwork] = useState<SwedishRoadNetwork>(EMPTY);
  const [isLoading, setIsLoading] = useState(false);
  const lastFetchedBoundsRef = useRef<MapBounds | null>(null);

  useEffect(() => {
    if (!active || zoom < 11 || !currentViewBounds) {
      if (!active) { setNetwork(EMPTY); lastFetchedBoundsRef.current = null; }
      return;
    }
    // Sweden-only data — skip if clearly outside SE.
    if (viewportCountry && viewportCountry !== 'SE') {
      setNetwork(EMPTY);
      return;
    }
    if (lastFetchedBoundsRef.current && boundsOverlapRatio(lastFetchedBoundsRef.current, currentViewBounds) > 0.7) {
      return;
    }

    const load = async () => {
      setIsLoading(true);
      try {
        const result = await fetchSwedishRoadNetwork(currentViewBounds);
        setNetwork(result);
        lastFetchedBoundsRef.current = currentViewBounds;
      } catch (err) {
        console.error('Failed to load Swedish road network:', err);
      } finally {
        setIsLoading(false);
      }
    };

    const timer = setTimeout(load, 700);
    return () => clearTimeout(timer);
  }, [currentViewBounds, active, zoom, viewportCountry]);

  return { network, isLoading };
};
